import { SidePanelLayerOrGroup, SidePanelLayerType } from "@nova/constants"
import { MergedFlight } from "@nova/protobuf"
import {
    selectMergedFlights,
    selectMergedFlightById,
} from "features/entity-caching/mergedFlights/selectors"
import { zoomToMergedHotspots } from "features/map/inspector-panel/hotspots/mergedFlight.ts/module"
import {
    updatedMergedFlight,
    deleteMergedFlight,
} from "features/pubsub/entityActions/cubes/mergedFlights"
import { PluginRegistry, PluginSpec } from "plugins/plugin-registry"
import * as Icons from "@nova/component-icons"
import { getMergedHotspotInspectorPanel } from "features/map/inspector-panel/hotspots/InspectorPanelHotspots"
import { ContextMenuItem } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    useMapActions,
    useMoveActions,
} from "panels/content/side-panel/generics/genericContextMenuSections"
import {
    selectSelectedLayerIds,
    setRenamingLayerId,
    removeLayerFromSelection,
} from "panels/content/side-panel/slice.new"
import { selectHotspotFlightIdsForMergedFlightById } from "features/entity-caching/images/hotspots/selectors"
import {
    updateInfraredFlight,
    deleteInfraredFlight,
} from "features/pubsub/entityActions/flights/infrared"
import { createNewLayerFractionalIndex } from "@nova/serializer/src/client/util/fractional_indexing"
import { selectProject } from "features/entity-caching/project/module"
import { useCallback } from "react"
import {
    getGenericConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import { getGenericAssetsListItem } from "panels/content/side-panel/generics/genericAssetsListItems"
import { MergedFlightAssetsThumbnail } from "panels/content/side-panel/assets-thumbnails/MergedFlightAssetsThumbnail"
import { useArchiveContextMenuOption } from "panels/content/side-panel/generics/useArchiveContextMenuOption"
import { useIsEditor } from "tools/hooks/useIsEditor"

export function registerMergedHotspotsPlugin(registry: PluginRegistry) {
    registry
        .register({
            name: "mergedInfrared",
            displayName: "Merged Hotspots",
            sidePanelLayerType: SidePanelLayerType.MergedFlight,
            PrimaryIcon: Icons.Merge,
            updateAction: updatedMergedFlight,
            deleteAction: deleteMergedFlight,
            confirmDelete: () => true,
            protobufMessage: MergedFlight,
            selectAllEntities: selectMergedFlights,
            selectSingleEntity: selectMergedFlightById,
            zoomTo: zoomToMergedHotspots,
            duplicate: () => undefined,
            search: (input, entity) =>
                entity.name.toLowerCase().includes(input.toLowerCase()) ? entity.name : undefined,
        })
        .implement("side-panel-layers-plugin", (spec) => ({
            filterKey: "Hotspots", // Uses the same layer filter as regular hotspots
            useContextMenuSections: getUseMergedHotspotsContextMenuSections(spec),
        }))
        .implement("side-panel-assets-plugin", (spec) => ({
            categoryId: "infrared", // In the same category as hotspots
            useThisCategoryHeader: false,
            SidePanelListItem: getGenericAssetsListItem({
                spec,
                ThumbnailRender: MergedFlightAssetsThumbnail,
            }),
        }))
        .implement("inspector-panel-plugin", (spec) => ({
            InspectorPanelComponent: getMergedHotspotInspectorPanel(spec),
            useFooterActions: (id: string) => {
                const entity = useAppSelector(spec.selectSingleEntity(id))
                const unmerge = useUnmergeMergedHotspots(id)
                const deleteAll = useDeleteMergedHotspots(id)
                const confirmationModal = useConfirmationModalContext()
                const isEditor = useIsEditor()
                if (!isEditor) return []
                return [
                    {
                        text: "Unmerge",
                        onClick: unmerge,
                        leftIcon: Icons.Unmerge,
                    },
                    {
                        text: "Delete",
                        onClick: async () => {
                            const didConfirm = await confirmationModal.confirm({
                                RenderComponent: getGenericConfirmationModalComponent({
                                    title: "Delete Selection?",
                                    message: `This will delete ${entity.name}, do you want to proceed?`,
                                }),
                            })
                            if (!didConfirm) return
                            deleteAll()
                        },
                        rightIcon: Icons.Trash,
                        isDestructive: true,
                    },
                ]
            },
        }))
}

function useDeleteMergedHotspots(id: string) {
    const dispatch = useAppDispatch()
    const hotspotFlightIds = useAppSelector(selectHotspotFlightIdsForMergedFlightById(id))
    return useCallback(() => {
        hotspotFlightIds.forEach((flightId) => {
            dispatch(deleteInfraredFlight({ id: flightId }))
        })
        dispatch(deleteMergedFlight({ id }))
    }, [dispatch, hotspotFlightIds, id])
}

function useUnmergeMergedHotspots(id: string) {
    const dispatch = useAppDispatch()
    const hotspotFlightIds = useAppSelector(selectHotspotFlightIdsForMergedFlightById(id))
    const project = useAppSelector(selectProject)
    const mergedFlight = useAppSelector(selectMergedFlightById(id))
    const unmerge = useCallback(() => {
        const parentId =
            !mergedFlight?.parentId || mergedFlight.parentId === "null"
                ? undefined
                : mergedFlight.parentId
        hotspotFlightIds.forEach((id, idx) => {
            dispatch(
                updateInfraredFlight({
                    id,
                    updates: {
                        mergeId: "",
                        parentId: parentId || "null",
                        index: createNewLayerFractionalIndex(project, idx, parentId),
                    },
                })
            )
        })
        dispatch(deleteMergedFlight({ id }))
    }, [dispatch, hotspotFlightIds, id, project, mergedFlight])
    return unmerge
}

function getUseMergedHotspotsContextMenuSections(spec: PluginSpec<MergedFlight>) {
    const useContextMenuSections = (id: string, sortedEntities: SidePanelLayerOrGroup[]) => {
        const dispatch = useAppDispatch()
        const entity = useAppSelector(spec.selectSingleEntity(id))
        const confirmationModal = useConfirmationModalContext()
        const unmerge = useUnmergeMergedHotspots(id)
        const deleteAll = useDeleteMergedHotspots(id)
        const selectedLayerIds = useAppSelector(selectSelectedLayerIds)
        const isEditor = useIsEditor()
        const actions = [] as ContextMenuItem[][]
        if (isEditor) {
            const layerActions: ContextMenuItem[] = []
            layerActions.push({
                title: "Rename",
                onClick: () => {
                    dispatch(setRenamingLayerId({ id }))
                },
                Icon: Icons.Pencil,
            })
            layerActions.push({
                title: "Unmerge",
                onClick: unmerge,
                Icon: Icons.Unmerge,
            })
            actions.push(layerActions)
        }
        const mapActions = useMapActions({
            spec,
            id,
        })
        actions.push(mapActions)
        const moveActions = useMoveActions({
            spec,
            id,
            mode: "Layers",
            sortedEntities,
        })
        if (moveActions.length) {
            actions.push(moveActions)
        }
        const archive = useArchiveContextMenuOption(id)
        if (archive) {
            actions.push([archive])
        }
        if (isEditor) {
            actions.push([
                {
                    title: "Delete",
                    onClick: async () => {
                        const didConfirm = await confirmationModal.confirm({
                            RenderComponent: getGenericConfirmationModalComponent({
                                title: "Delete Selection?",
                                message: `This will delete ${entity.name}, do you want to proceed?`,
                            }),
                        })
                        if (!didConfirm) return
                        deleteAll()
                        if (selectedLayerIds.includes(id)) {
                            dispatch(removeLayerFromSelection({ id }))
                        }
                    },
                    Icon: Icons.Trash,
                    isDestructive: true,
                },
            ])
        }
        return actions
    }
    return useContextMenuSections
}
